import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Radar, Search, Link2, PackageSearch } from "lucide-react";
import { toast } from "sonner";
import { TrackingDetail } from "@/components/TrackingDetail";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicTrack() {
  const { awb: awbParam } = useParams();
  const navigate = useNavigate();
  const [awbInput, setAwbInput] = useState(awbParam || "");
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (awb) => {
    if (!awb) return;
    setLoading(true);
    setError("");
    setDetail(null);
    try {
      const res = await axios.get(`${API}/api/public/track/${encodeURIComponent(awb)}`);
      setDetail(res.data);
    } catch (e) {
      setError(e.response?.status === 404 ? "not-found" : "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (awbParam) {
      setAwbInput(awbParam);
      load(awbParam);
    }
  }, [awbParam, load]);

  const submit = (e) => {
    e.preventDefault();
    const q = awbInput.trim();
    if (!q) return;
    navigate(`/track/${encodeURIComponent(q)}`);
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(window.location.href);
    toast.success("Tracking link copied — share it with your customer.");
  };

  return (
    <div className="min-h-screen bg-paper text-obsidian" data-testid="public-track-page">
      {/* header */}
      <header className="bg-obsidian text-white">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-black text-lg" data-testid="public-track-logo">
            <img src="/logo.png" alt="ShipZilla logo" className="w-8 h-8 rounded-xl" />
            SHIP<span className="text-acid">ZILLA</span>
          </Link>
          <p className="text-[11px] font-mono font-bold uppercase tracking-[0.25em] text-sky-300 hidden sm:block">Shipment Tracking</p>
        </div>
      </header>

      {/* search strip */}
      <div className="bg-obsidian text-white border-t border-white/10">
        <div className="max-w-5xl mx-auto px-5 py-10 sm:py-14">
          <h1 className="font-display font-black tracking-tight text-3xl sm:text-5xl">
            Where's my <span className="text-acid">parcel?</span>
          </h1>
          <p className="text-sm text-zinc-400 mt-3 max-w-xl">Enter your AWB / tracking number to see the live courier status and full scan history — straight from ShipZilla's control tower.</p>
          <form onSubmit={submit} className="mt-6 flex gap-2 max-w-xl" data-testid="public-track-form">
            <div className="flex items-center gap-2 bg-white text-obsidian px-4 py-3 border-2 border-obsidian flex-1 shadow-[4px_4px_0px_0px_#2563EB]">
              <Search className="w-4 h-4 text-zinc-400 shrink-0" />
              <input value={awbInput} onChange={(e) => setAwbInput(e.target.value)} placeholder="AWB / tracking number"
                data-testid="public-track-input" className="w-full text-sm outline-none bg-transparent font-mono" />
            </div>
            <button type="submit" data-testid="public-track-submit"
              className="bg-acid text-white font-bold px-6 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_rgba(255,255,255,0.25)] hover:shadow-none hover:translate-x-[3px] hover:translate-y-[3px] transition-all duration-150 text-xs uppercase tracking-widest">
              Track
            </button>
          </form>
        </div>
      </div>

      {/* result */}
      <main className="max-w-5xl mx-auto px-5 py-8 sm:py-12">
        {loading && (
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400 flex items-center gap-2" data-testid="public-track-loading">
            <Radar className="w-4 h-4 animate-spin" /> Contacting courier…
          </p>
        )}

        {error && !loading && (
          <div className="border-2 border-obsidian bg-white p-8 max-w-xl shadow-[6px_6px_0px_0px_#2563EB]" data-testid="public-track-error">
            <PackageSearch className="w-8 h-8 text-blaze" />
            <p className="font-bold text-lg mt-3">{error === "not-found" ? "We couldn't find that AWB." : "Tracking is unavailable right now."}</p>
            <p className="text-sm text-zinc-500 mt-2">
              {error === "not-found"
                ? "Check the number on your shipping confirmation and try again. New shipments can take a few hours to appear."
                : "Please retry in a moment."}
            </p>
          </div>
        )}

        {detail && !loading && (
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <p className="text-[11px] font-mono font-bold uppercase tracking-[0.2em] text-zinc-500">
                Live status · updates automatically with courier scans
              </p>
              <button onClick={copyLink} data-testid="public-track-copy-link"
                className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold uppercase tracking-widest px-4 py-2 border-2 border-obsidian bg-white hover:bg-obsidian hover:text-sky-300 transition-colors duration-150">
                <Link2 className="w-3.5 h-3.5" /> Copy tracking link
              </button>
            </div>
            <TrackingDetail detail={detail} publicView />
          </div>
        )}

        {!detail && !loading && !error && (
          <div className="text-center py-10" data-testid="public-track-idle">
            <Radar className="w-10 h-10 text-zinc-300 mx-auto" />
            <p className="text-sm text-zinc-400 mt-4">Your shipment journey will appear here.</p>
          </div>
        )}
      </main>

      {/* footer */}
      <footer className="bg-obsidian text-white mt-8">
        <div className="max-w-5xl mx-auto px-5 py-8 flex items-center justify-between flex-wrap gap-4">
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-400">
            Powered by <span className="text-sky-300 font-bold">SHIPZILLA.IN</span> · Smart logistics for growing brands
          </p>
          <Link to="/" data-testid="public-track-cta"
            className="text-[11px] font-bold uppercase tracking-widest bg-acid text-white px-5 py-2.5 border-2 border-obsidian hover:bg-obsidian hover:text-sky-300 transition-colors duration-150">
            Ship with ShipZilla →
          </Link>
        </div>
      </footer>
    </div>
  );
}
